import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Button, Stack, styled, TextField } from "~/common/mui";
import { useCountryOptionsWithUrl } from "~/features/cart/B2CRequestPage/utils";
import { TextWithHighlight } from "~/common/components/TextWithHighlight";
import { getRegionCookie, setRegionCookie } from "~/features/forkPage/utils/regionCookie";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { fetchRegionSiteAndRedirect } from "~/features/regionSelection/components/RegionSelectionBanner";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { MobileCountrySelector } from "~/features/forkPage/components/MobileCountrySelector";

export interface formInputs {
  country: string;
}

export const CountrySelector = () => {
  const { t } = useTranslation();
  const isTablet = useIsMinTabletSize();

  const cookieId = getRegionCookie();
  const countryOptions = useCountryOptionsWithUrl();

  const defaultCountry = cookieId?.length > 0 ? cookieId : window.SCHA.regionSelectionBanner?.dropdownUrls?.[0]?.url;

  const [selectedCountry, setSelectedCountry] = useState<string>(defaultCountry ?? "");
  const [inputValue, setInputValue] = useState<string>(defaultCountry || "");
  const { val: isOpen, setTrue: openDrawer, setFalse: closeDrawer } = useBooleanState(false);

  const options = countryOptions.map((item) => item.stringLabel ?? "");
  const optionMap = Object.fromEntries(countryOptions.map((item) => [item.stringLabel ?? "", item]));
  const valueMap = Object.fromEntries(countryOptions.map((item) => [item.value ?? "", item]));

  const handleOnChange = useCallback(
    (_event, newLabel, reason) => {
      if (reason === "clear" || !newLabel) {
        setSelectedCountry("");
        setInputValue("");
        return;
      }
      const selected = optionMap[newLabel];
      if (selected) {
        setSelectedCountry(selected.value);
        setInputValue(selected.stringLabel ?? "");
      }
    },
    [optionMap],
  );

  const handleCountryRedirect = (countryCode: string) => {
    const selected = countryOptions.find((c) => c.value === countryCode);

    if (selected && selected?.url?.length > 0) {
      setRegionCookie(countryCode);

      const url = new URL(selected.url);
      if (selected.isRedirectedCountry) {
        url.searchParams.set("redirectedCountry", countryCode);
      }
      window.location.href = url.toString();
    } else {
      const fallbackUrl = window.SCHA.regionSelectionBanner?.dropdownUrls?.[0]?.url;
      setRegionCookie(fallbackUrl);
      fetchRegionSiteAndRedirect(fallbackUrl);
    }
  };

  const onSubmit = () => {
    handleCountryRedirect(selectedCountry);
  };

  const handleMobileApply = (label: string) => {
    const matched = countryOptions.find((c) => c.label === label);
    handleCountryRedirect(matched?.value ?? "");
  };

  const handleMobileAutocompleteOpen = () => {
    if (!isTablet) {
      openDrawer();
    }
  };

  return (
    <>
      {isOpen ? (
        <MobileCountrySelector
          selectedCountry={selectedCountry}
          closeDrawer={closeDrawer}
          handleApply={handleMobileApply}
        />
      ) : (
        <Container direction={"row"} gap={1}>
          <StyledAutocomplete
            sx={{ width: 305, flex: 1 }}
            value={valueMap[selectedCountry]?.stringLabel ?? ""}
            inputValue={inputValue}
            onInputChange={(_e, value) => setInputValue(value)}
            onChange={handleOnChange}
            options={options}
            disableClearable={selectedCountry.length < 2}
            autoHighlight
            getOptionLabel={(value) => optionMap[value as string]?.stringLabel || ""}
            onOpen={handleMobileAutocompleteOpen}
            noOptionsText={t("forkPage.country.search.noresults")}
            loadingText={t("spinner.text")}
            renderOption={(props, label) => (
              <li {...props} key={`key-${label}`}>
                <TextWithHighlight highlight={inputValue} highlightClass={"ellipsis"}>
                  {label as string}
                </TextWithHighlight>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label={t("forkPage.country.input")} />}
          />
          <ApplyButton size={"medium"} className="ellipsis" onClick={onSubmit} disabled={selectedCountry.length < 2}>
            {t("forkPage.apply.btn")}
          </ApplyButton>
        </Container>
      )}
    </>
  );
};

const Container = styled(Stack)({
  flexWrap: "wrap",
  alignItems: "center",
});

const ApplyButton = styled(Button)({
  flexShrink: 0,
});

export const StyledAutocomplete = styled(Autocomplete)({
  "& .MuiAutocomplete-input": {
    marginRight: "32px !important",
  },
});
